import { useAuth } from '@clerk/react';
import {
  Box,
  Center,
  Group,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { openSpotlight } from '@mantine/spotlight';
import type { SelectItem } from '@vrplatform/components';
import { Logo, VrpDarkLogo } from '@vrplatform/components-legacy';
import {
  fetchApiAllItems,
  getImpersonationActorTeamId,
  useApiQuery,
  useDashboard,
  useMe,
  useOwnerPortal,
  useOwnerPortalContact,
  useOwnerPortalPreviewSession,
  useQuery,
  useRegionalCrossTeamApiResolver,
  useTeam,
  useTeamIdContext,
  useTeamRole,
  useTeamSwitch,
  useIsOwnerPortalPreview,
} from '@vrplatform/data';
import { useAppName } from '@vrplatform/theme';
import { formatOwnerName } from '@vrplatform/ui-common';
import { hasValue, isUUID, sortBy } from '@vrplatform/utils';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { TeamSwitch, TeamSwitchSelect } from '../../../components/team-switch';
import { useNavbarExpanded } from '../../../hooks';
import { useOwnerPortalSelectedContact } from '../../../routes/dashboard/useOwnerPortalSelectedContact';
import {
  groupNavbarOwnerSwitchItems,
  resolveOwnerSwitchScopedTeamId,
} from './NavbarTeamSwitch.utils';

export const NavbarTeamSwitch = () => {
  const { isDesktopExpanded } = useNavbarExpanded();

  const [dashboard] = useDashboard();

  return (
    <Group
      wrap="nowrap"
      justify={isDesktopExpanded ? 'flex-start' : 'center'}
      sx={{
        flex: 1,
        flexDirection: isDesktopExpanded ? undefined : 'column',
      }}
    >
      <DashboardLogo />
      {dashboard === 'owner' ? (
        <OwnerSwitch isNavigationExpanded={isDesktopExpanded} />
      ) : (
        <TeamSwitch variant="navbar" isNavigationExpanded={isDesktopExpanded} />
      )}
    </Group>
  );
};

const DashboardLogo = () => {
  const { isDesktopExpanded } = useNavbarExpanded();
  const { isVrpAdmin } = useTeamRole();

  const [team] = useTeam();
  const [dashboard] = useDashboard();
  const logo = dashboard === 'owner' ? team.logo : undefined;

  const { white } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Group
      wrap="nowrap"
      sx={{
        flexShrink: 0,
        justifyContent: isDesktopExpanded ? 'flex-start' : 'center',
        flexDirection: isDesktopExpanded ? undefined : 'column',
      }}
      w={40}
    >
      <Box
        onClick={() => isVrpAdmin && openSpotlight()}
        sx={{
          backgroundColor: colorScheme === 'dark' ? undefined : white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 5,
          borderRadius: 10,
          cursor: isVrpAdmin ? 'pointer' : undefined,
        }}
      >
        {colorScheme === 'dark' && dashboard !== 'owner' ? (
          <VrpDarkLogo />
        ) : (
          <Logo img={logo} />
        )}
      </Box>
    </Group>
  );
};

type ProductGroup = 'VR Trust' | 'VR Platform';

function useOwnerQuery(
  search: string,
  {
    isUserPreview,
    teamId,
    isPreviewTeamSwitchPending,
  }: {
    isUserPreview: boolean;
    teamId: string | null | undefined;
    isPreviewTeamSwitchPending: boolean;
  }
) {
  const { id: meId } = useMe();
  const [{ isGL }] = useTeam();
  const portal = useOwnerPortal();
  const { contactId } = useOwnerPortalContact();
  const getRegionalCrossTeamApi = useRegionalCrossTeamApiResolver();
  const scopedTeamId = resolveOwnerSwitchScopedTeamId({
    isUserPreview,
    portalTeamId: portal?.teamId,
    teamId,
  });

  const isGLOwnerPreview = isGL && isUserPreview;

  const ownerAccessQuery = useQuery(
    (q, args) => {
      if (
        args.isUserPreview &&
        (args.isPreviewTeamSwitchPending || !args.scopedTeamId)
      )
        return [];

      return q
        .ownerUserAccesses({
          where: args.scopedTeamId
            ? {
                userId: { _eq: args.meId },
                owner: {
                  tenantId: { _eq: args.scopedTeamId },
                },
              }
            : {
                userId: { _eq: args.meId },
                owner: {},
              },
        })
        .map((x) => {
          const label = formatOwnerName(x.owner, { showEmpty: true });
          const teamName = x.owner?.tenant?.name;
          const logo = x.owner?.tenant?.logo;
          const ownerId = x.ownerId || '-';
          const tenantId = x.owner?.tenantId || '-';
          const productGroup = 'VR Platform' as ProductGroup;

          return {
            value: ownerId,
            label: label || '-',
            description: portal?.teamId === tenantId ? portal.name : teamName,
            icon: (
              <Center w={26}>
                <Logo img={logo} width={26} radius="xl" />
              </Center>
            ),
            tenantId,
            productGroup,
          } satisfies SelectItem & {
            tenantId: string;
            productGroup: ProductGroup;
          };
        });
    },
    {
      variables: {
        meId,
        isUserPreview,
        scopedTeamId,
        isPreviewTeamSwitchPending,
      },
      skip:
        !meId ||
        (isUserPreview && (isPreviewTeamSwitchPending || !teamId)) ||
        isGLOwnerPreview,
      teamScoped: false,
    }
  );

  const contactQuery = useApiQuery(
    async (api, signal, args) => {
      const teams = await fetchApiAllItems(async (page) => {
        const response = await api.GET('/me/teams', {
          params: {
            query: {
              ids: args.scopedTeamId ?? undefined,
              limit: 200,
              page,
            },
          },
          signal,
        });

        if (response.error) throw response.error;

        return {
          items: response.data.data.filter((team) => team.isGeneralLedger),
          hasNextPage: !!response.data.pagination.nextPage,
        };
      });

      return await Promise.all(
        teams.map(async (team) => {
          const regionalApi = getRegionalCrossTeamApi(team.apiBaseUrl);
          const headers = {
            'x-data-region': team.dataRegion,
            'x-team-id': team.id,
          };
          const [teamResponse, contacts] = await Promise.all([
            regionalApi.GET('/team', {
              headers,
              signal,
            }),
            fetchApiAllItems(async (page) => {
              const response = await regionalApi.GET('/contacts', {
                params: {
                  query: {
                    limit: 200,
                    page,
                    type: 'owner',
                  },
                },
                headers,
                signal,
              });

              if (response.error) throw response.error;

              return {
                items: response.data.data,
                hasNextPage: !!response.data.pagination.nextPage,
              };
            }),
          ]);

          if (teamResponse.error) throw teamResponse.error;

          return contacts.map((contact) => {
            return {
              value: contact.id,
              label: formatOwnerName(contact, { showEmpty: true }) || '-',
              description: portal?.teamId === team.id ? portal.name : team.name,
              icon: (
                <Center w={26}>
                  <Logo img={teamResponse.data.logo} width={26} radius="xl" />
                </Center>
              ),
              tenantId: team.id,
              productGroup: 'VR Trust' as ProductGroup,
            } satisfies SelectItem & {
              tenantId: string;
              productGroup: ProductGroup;
            };
          });
        })
      ).then((data) => data.flat());
    },
    {
      variables: {
        isPreviewTeamSwitchPending,
        isUserPreview,
        meId,
        scopedTeamId,
      },
      skip:
        !meId ||
        !teamId ||
        (isUserPreview && isPreviewTeamSwitchPending) ||
        isGLOwnerPreview,
      queryGroup: 'owners',
      uniqueQueryKey: 'navbar-contact-switch-options',
      queryKeys: ['contacts', 'teams'],
      teamScoped: false,
    }
  );

  const selectedContactQuery = useOwnerPortalSelectedContact({
    skip: !isGLOwnerPreview,
  });

  const options = useMemo(() => {
    return sortBy(
      [...(contactQuery.data ?? []), ...(ownerAccessQuery.data ?? [])],
      (x) => `${x.productGroup}-${x.label}`
    );
  }, [contactQuery.data, ownerAccessQuery.data]);

  const visibleOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filteredOptions = normalizedSearch
      ? options.filter((option) =>
          option.label.toLowerCase().includes(normalizedSearch)
        )
      : options;

    return groupNavbarOwnerSwitchItems(filteredOptions);
  }, [options, search]);

  return {
    options,
    // A skipped query can still return the owner routes' cached contact.
    value:
      isGLOwnerPreview && selectedContactQuery.data
        ? ({
            value: selectedContactQuery.data.id,
            label:
              formatOwnerName(selectedContactQuery.data, { showEmpty: true }) ||
              '-',
          } satisfies SelectItem)
        : (options.find((option) => option.value === contactId) ?? null),
    canSwitch: options.length > 1,
    loadingValue:
      contactQuery.isLoading ||
      ownerAccessQuery.isLoading ||
      selectedContactQuery.isLoading,
    queryData: {
      data:
        ownerAccessQuery.data || contactQuery.data
          ? {
              pages: [
                {
                  list: visibleOptions,
                  aggregate: visibleOptions.length,
                },
              ],
              pageParams: [],
            }
          : undefined,
      error: contactQuery.error ?? ownerAccessQuery.error ?? null,
      isFetching: contactQuery.isFetching || ownerAccessQuery.isFetching,
      isFetchingNextPage: false,
      hasNextPage: false,
      fetchNextPage: async () => undefined,
    },
  };
}

const OwnerSwitch = ({
  isNavigationExpanded,
}: {
  isNavigationExpanded: boolean;
}) => {
  const { actor } = useAuth();
  const [searchParams] = useSearchParams();

  const { appName } = useAppName();
  const portal = useOwnerPortal();

  const [team] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const setTeamId = useTeamSwitch();
  const goto = useNavigate();

  const [search, setSearch] = useState('');
  const isUserPreview = useIsOwnerPortalPreview();
  const sti = getImpersonationActorTeamId(actor) ?? searchParams.get('sti');
  const teamScopeId = selectedTeamId ?? team?.id;
  const isPreviewTeamSwitchPending =
    isUserPreview && !!sti && isUUID(sti) && sti !== teamScopeId;
  // A token preview shows one owner; the switch has no owners to offer.
  const isSwitchDisabled = !!useOwnerPortalPreviewSession();

  const { setContactId } = useOwnerPortalContact();
  const { queryData, canSwitch, loadingValue, value, options } = useOwnerQuery(
    search,
    {
      isUserPreview,
      teamId: teamScopeId,
      isPreviewTeamSwitchPending,
    }
  );

  const setNewOwner = useCallback(
    async (newValue: SelectItem | null) => {
      if (isPreviewTeamSwitchPending) return;
      if (!newValue?.value) return;

      const tenantId = options.find(
        (option) => option.value === newValue.value
      )?.tenantId;

      if (!tenantId) return;
      if (isUserPreview && teamScopeId && tenantId !== teamScopeId) return;

      if (!isUserPreview) {
        await setTeamId(tenantId);
      }
      setContactId(newValue.value);

      const hasKeys = [...searchParams.values()].filter(hasValue).length > 0;
      if (hasKeys && !isPreviewTeamSwitchPending) {
        goto(window.location.pathname);
      }
    },
    [
      isPreviewTeamSwitchPending,
      isUserPreview,
      teamScopeId,
      setTeamId,
      searchParams,
      goto,
      setContactId,
      options,
    ]
  );

  return (
    <TeamSwitchSelect
      value={value ?? null}
      canSwitch={!isSwitchDisabled && !isPreviewTeamSwitchPending && canSwitch}
      disabled={isSwitchDisabled}
      loadingQuery={loadingValue || isPreviewTeamSwitchPending}
      queryData={queryData}
      header={portal?.name || team?.name || appName}
      isNavigationExpanded={isNavigationExpanded}
      setSearch={setSearch}
      setValue={setNewOwner}
    />
  );
};
